// RITA-1547 — Texte zum Entwurf „Vertrag erstellen“, wie im Bildentwurf 3b (Bilder A–G) von Julia am
// 20.10.2026 freigegeben. Zeiten sind Zeitpunkte und stehen in der Ortszeit des Browsers wie die übrigen
// Zeitangaben (vgl. features/templates/services/sperrzeit.ts).
// RITA-1552: Reiter- und Leer-Text kennen auch das Anschreiben Rücksendung. Texte, die je Vorgang abweichen
// (Rückfrage „Es gibt schon einen Entwurf“, Entwurfszeile, Seitentitel, Pfad, „kein Entwurf“), nehmen den Vorgang mit
// dem Vertrag als Vorgabe – für ihn bleiben sie wortgleich mit 3b/3c (Skizze Rücklauf, Bild E und D a; Festlegung
// Julia 22.10.2026: Rückfrage der Rücksendung im 3b-Muster, Entwurfszeile ohne „– noch nicht gedruckt“ wie 3c-F13).
import type { ContractCreationKind } from 'global-intern-types';
import type { Vorgang } from './vorgang';
import { VERTRAGSART_EINTRAGEN, VERTRAGSART_FEHLT } from './vertragserstellungTexte';

/** „20.10.2026, 14:05“ */
export function entwurfszeit(iso: string): string {
  const zeit = new Date(iso);
  if (Number.isNaN(zeit.getTime())) return 'unbekannter Zeit';
  return `${zeit.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}, ${zeit.toLocaleTimeString(
    'de-DE',
    { hour: '2-digit', minute: '2-digit' },
  )}`;
}

/** „21.10.2026“ */
export function entwurfsdatum(iso: string): string {
  const zeit = new Date(iso);
  if (Number.isNaN(zeit.getTime())) return 'unbekanntem Tag';
  return zeit.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Wer zuletzt gespeichert bzw. gedruckt hat; fehlt die Person (gelöscht), steht „unbekannt“. */
export const person = (name: string | null | undefined) => (name ?? '').trim() || 'unbekannt';

// Bild A – Fenster „Vertrag erstellen“
export const BEARBEITEN_KNOPF = 'Bearbeiten';
/**
 * RITA-1549 (Skizze 3c, Bild A): Fußsatz des Fensters, seit „Drucken“ dazugehört. Der Satz steht hier und nicht in
 * vertragsdruckTexte.ts, weil jene Datei die Zeit- und Prüftexte von hier braucht und ESLint keine Import-Zyklen
 * zulässt; vertragsdruckTexte.ts reicht die Konstante unter demselben Namen weiter.
 */
export const FENSTER_FUSS_DRUCK =
  '„Drucken“ erstellt eine PDF, öffnet sie in einem neuen Tab, legt sie unter „Dokumente“ ab und trägt sie ins Aktionsprotokoll ein.';

// Bild B – Es gibt schon einen Entwurf
export const VORHANDEN_TITEL = 'Es gibt schon einen Entwurf';
/** RITA-1552: bei der Rücksendung „Es gibt einen Entwurf der Rücksendung vom …“ (3b-Muster, gleicher Titel). */
export const vorhandenText = (zeit: string, name: string | null, vorgang: Vorgang = 'contract') =>
  `Es gibt einen Entwurf${vorgang === 'return' ? ' der Rücksendung' : ''} vom ${entwurfszeit(zeit)} (zuletzt gespeichert von ${person(
    name,
  )}) – weiterbearbeiten oder neu beginnen?`;
export const VORHANDEN_HINWEIS =
  'Neu beginnen: Das PRM sucht die Vorlagen neu aus und setzt die aktuellen Daten ein. Der bisherige Entwurf wird erst ersetzt, wenn du den neuen speicherst.';
export const ABBRECHEN = 'Abbrechen';
export const NEU_BEGINNEN = 'Neu beginnen';
export const WEITERBEARBEITEN = 'Weiterbearbeiten';

// Bild C – Bearbeiten-Seite
export const PFAD_BERATER = 'Berater';
export const PFAD_VERTRAG_ERSTELLEN = 'Vertrag erstellen';
/** RITA-1552 (Bild E): „Berater › Anna Muster › Rücksendung drucken › Bearbeiten“. */
export const PFAD_RUECKSENDUNG = 'Rücksendung drucken';
/** Pfadteil vor „Bearbeiten“ je Vorgang. */
export const pfadVorgang = (vorgang: Vorgang = 'contract') =>
  vorgang === 'return' ? PFAD_RUECKSENDUNG : PFAD_VERTRAG_ERSTELLEN;
/** „Vertrag erstellen – Anna Muster“; RITA-1552 (Bild E): „Rücksendung drucken – Anna Muster“. */
export const seitenTitel = (beraterName: string, vorgang: Vorgang = 'contract') =>
  `${pfadVorgang(vorgang)} – ${beraterName}`;
export const PFAD_BEARBEITEN = 'Bearbeiten';
export const ZURUECK_ZUM_BERATER = 'Zurück zum Berater';
export const ENTWURF_SPEICHERN = 'Entwurf speichern';
export const WIRD_GESPEICHERT = 'Wird gespeichert …';
export const gespeichertAmText = (zeit: string) => `Entwurf gespeichert am ${entwurfszeit(zeit)}`;
export const NICHT_GESPEICHERTE_AENDERUNGEN = 'Nicht gespeicherte Änderungen';
export const NOCH_NICHT_GESPEICHERT = 'Noch nicht gespeichert';
export const KEINE_VORLAGE_REITER: Record<ContractCreationKind, string> = {
  dispatch_letter: 'Anschreiben: keine passende Vorlage',
  contract: 'Vertrag: keine passende Vorlage',
  return_letter: 'Rücksendung: keine passende Vorlage',
};
export const SEITE_WIRD_GELADEN = 'Entwurf wird geladen …';
export const SEITE_LADEFEHLER = 'Der Entwurf konnte nicht geladen werden.';
export const keinEntwurfText = (beraterName: string, vorgang: Vorgang = 'contract') =>
  `Für ${beraterName} gibt es keinen gespeicherten Entwurf${vorgang === 'return' ? ' der Rücksendung' : ''} (mehr).`;

// Bild D – Karte mit Entwurf
/** RITA-1552 (Bild E): die Zeile des Rücksendungs-Entwurfs heißt „Entwurf Rücksendung vom … · <Name>“. */
export const entwurfZeileTitel = (zeit: string, name: string | null, vorgang: Vorgang = 'contract') =>
  `Entwurf${vorgang === 'return' ? ' Rücksendung' : ''} vom ${entwurfszeit(zeit)} · ${person(name)}`;
/** RITA-1549 (F13): nur noch die Vorlagennamen; ob gedruckt wurde, zeigt das Aktionsprotokoll darunter. */
export const entwurfZeileInhalt = (vorlagen: string[]) => vorlagen.join(' + ');
export const VERWERFEN = 'Verwerfen';
export const VERWERFEN_TITEL = 'Entwurf verwerfen?';
export const verwerfenText = (zeit: string, name: string | null) =>
  `Der Entwurf vom ${entwurfszeit(zeit)} (${person(name)}) wird gelöscht. Das lässt sich nicht rückgängig machen. Die Vorlagen bleiben unverändert.`;
/** Verwerfen traf auf einen inzwischen neu gespeicherten Stand; die Rückfrage zeigt ihn erneut. */
export const verwerfenNeuerStandText = (name: string | null) =>
  `Inzwischen hat ${person(name)} den Entwurf neu gespeichert. Bitte prüfe, ob du ihn trotzdem verwerfen möchtest.`;
export const VERWERFEN_FEHLER = 'Der Entwurf konnte nicht verworfen werden. Bitte erneut versuchen.';
export const KARTE_ENTWURF_LADEFEHLER = 'Der Entwurf konnte nicht geladen werden.';
/**
 * RITA-1552: Ladefehler des Rücksendungs-Entwurfs in der Karte – eigener Wortlaut im Muster „… der Rücksendung“
 * (vorhandenText, keinEntwurfText), weil beide Hinweise zugleich erscheinen können. Nicht in der Skizze.
 */
export const KARTE_RUECKSENDUNGSENTWURF_LADEFEHLER = 'Der Entwurf der Rücksendung konnte nicht geladen werden.';

// Bild E – Vorlage oder Berater-Daten geändert
export const GEAENDERT_TITEL = 'Seit dem Entwurf hat sich etwas geändert';
export const vorlageGeaendertText = (vorlage: string, iso: string | null) =>
  iso
    ? `Die Vorlage „${vorlage}“ wurde am ${entwurfsdatum(iso)} geändert.`
    : `Die Vorlage „${vorlage}“ wurde geändert.`;
export const vorlageGeloeschtText = (vorlage: string) => `Die Vorlage „${vorlage}“ gibt es nicht mehr.`;
export const beraterdatenGeaendertText = (bezeichnungen: string[]) => `Berater-Daten: ${bezeichnungen.join(', ')}`;
export const GEAENDERT_HINWEIS =
  'Entwurf behalten: Der Entwurf bleibt genau so, wie er gespeichert ist. Neu beginnen: Das PRM sucht die Vorlagen neu aus und setzt die aktuellen Daten ein – deine Änderungen im Entwurf gehen dabei verloren, sobald du speicherst.';
export const GELOESCHT_HINWEIS = 'Der Entwurf lässt sich trotzdem weiterbearbeiten und später drucken.';
export const ENTWURF_BEHALTEN = 'Entwurf behalten';
/** Bild E c); ohne vorhandenen Entwurf (Seite neu aus den Vorlagen) entfällt der letzte Satz. */
export const keineVorlageText = (beraterName: string, kriterien: string, mitEntwurf = true) =>
  `Für ${beraterName} passt gerade keine Vertragsvorlage (${kriterien}).${
    mitEntwurf ? ' Der Entwurf bleibt unverändert.' : ''
  }`;
/** RITA-1572: wie keineVorlageText, wenn beim Berater keine Vertragsart eingetragen ist. */
export const vertragsartFehltText = (mitEntwurf = true) =>
  `${VERTRAGSART_FEHLT} ${VERTRAGSART_EINTRAGEN}${mitEntwurf ? ' Der Entwurf bleibt unverändert.' : ''}`;
export const OK = 'OK';

// Bild F – Seite verlassen
export const VERLASSEN_TITEL = 'Seite verlassen?';
export const VERLASSEN_TEXT = 'Deine Änderungen sind noch nicht gespeichert.';
export const WEITER_BEARBEITEN = 'Weiter bearbeiten';

// Bild G – Zwei Personen speichern nacheinander
export const KONFLIKT_TITEL = 'Entwurf wurde inzwischen gespeichert';
export const konfliktText = (name: string | null, zeit: string) =>
  `Der Entwurf wurde inzwischen von ${person(name)} gespeichert (${entwurfszeit(zeit)}).`;
export const konfliktHinweis = (name: string | null) =>
  `Gespeicherten Stand laden: Deine Änderungen gehen verloren. Trotzdem speichern: Der Stand von ${person(name)} wird ersetzt.`;
export const GESPEICHERTEN_STAND_LADEN = 'Gespeicherten Stand laden';
export const TROTZDEM_SPEICHERN = 'Trotzdem speichern';
export const VERWORFEN_TITEL = 'Entwurf wurde inzwischen verworfen';
export const VERWORFEN_TEXT = 'Jemand hat den Entwurf in der Zwischenzeit verworfen.';
export const VERWORFEN_HINWEIS = 'Trotzdem speichern: Dein Stand wird als neuer Entwurf gespeichert.';

// Speichern – Prüfungen vor dem Senden und Fehler
export const SPEICHERFEHLER = 'Der Entwurf konnte nicht gespeichert werden. Bitte erneut versuchen.';
export const PLATZHALTER_FEHLER =
  'Im Entwurf steht noch ein Platzhalter (gestricheltes Kästchen), zum Beispiel aus kopiertem Vorlagentext. Bitte lösche ihn und schreibe den Text direkt hinein.';
export const ZU_GROSS = 'Der Entwurf ist zu groß zum Speichern. Bitte kürzen.';
const LEERES_DOKUMENT: Record<ContractCreationKind, string> = {
  dispatch_letter: 'Das Anschreiben',
  contract: 'Der Vertrag',
  return_letter: 'Das Rücksendungs-Anschreiben',
};
export const leerText = (art: ContractCreationKind) => `${LEERES_DOKUMENT[art]} ist leer. Bitte einen Inhalt eingeben.`;
